import { ErrorMessage } from "@hookform/error-message";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { Card, CardBody, CardHeader } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { GET, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";

const RATE_FIELDS = [
  { key: "oilRecoveryFromPressRate", label: "Tỷ lệ thu hồi dầu ép" },
  { key: "refineFromPressedOilRate", label: "Tỷ lệ nấu từ dầu ép" },
  { key: "refineFromPurchasedOilRate", label: "Tỷ lệ nấu từ dầu mua" },
  { key: "burnRate", label: "Tỷ lệ đốt" },
  { key: "residueFromPressRate", label: "Tỷ lệ thu bã ép" },
  { key: "residuePelletRecoveryRate", label: "Tỷ lệ thu hồi viên nén bã điều từ bã điều" },
];

const DEFAULT_CONTENT = Object.fromEntries(RATE_FIELDS.map(({ key }) => [key, 0]));
const DEFAULT_FORM = { monthYear: "", content: DEFAULT_CONTENT };

const IngredientExchange = () => {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);

  const { control, handleSubmit, reset, errors } = useForm({
    defaultValues: DEFAULT_FORM,
  });

  const fetchConfigs = () => {
    GET(`/ingredient-exchange/config`, {}, true).then(({ data }) => {
      setConfigs(data || []);
    });
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const onSubmit = ({ monthYear, content }) => {
    setLoading(true);
    PUT(`/ingredient-exchange/config`, { monthYear, content }, {}, true)
      .then(() => {
        toast.success("Cập nhật thành công");
        fetchConfigs();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      })
      .finally(() => setLoading(false));
  };

  const onEditClick = (item) => {
    reset({
      monthYear: item.monthYear,
      content: { ...DEFAULT_CONTENT, ...item.content },
    });
  };

  const columns = [
    { Header: "Tháng hiệu lực", accessor: (item) => item.monthYear },
    ...RATE_FIELDS.map(({ key, label }) => ({
      Header: label,
      accessor: (item) => (
        <NumericFormat displayType="text" value={item.content?.[key] ?? 0} suffix=" %" decimalScale={2} />
      ),
    })),
    {
      id: "action",
      Header: "",
      accessor: (item) => (
        <a
          href="#edit"
          onClick={(e) => {
            e.preventDefault();
            onEditClick(item);
          }}
        >
          <Icon name="edit" />
        </a>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Head title="Tỉ lệ quy đổi nguyên liệu"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Danh mục tỷ lệ quy đổi nguyên liệu</BlockTitle>
              <p className="text-soft">
                Khi thay đổi tỷ lệ, báo cáo tồn kho quy đổi nguyên liệu (Vỏ → Dầu thô ép/mua → Dầu tinh + Bã) sẽ chạy theo tỷ lệ mới, áp dụng từ tháng hiệu lực trở đi cho đến khi có tỷ lệ mới hơn.
              </p>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row>
            <Col md={6}>
              <Card>
                <CardHeader className="bg-primary text-white">
                  Danh mục tỷ lệ
                </CardHeader>
                <CardBody>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Row className="g-3">
                      <Col md="12">
                        <div className="form-group">
                          <label className="form-label">
                            Tháng hiệu lực <span className="text-danger">*</span>
                          </label>
                          <Controller
                            control={control}
                            name="monthYear"
                            rules={{ required: "Vui lòng chọn tháng hiệu lực" }}
                            render={({ onChange, value }) => (
                              <input
                                type="month"
                                className="form-control"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="monthYear"
                            render={({ message }) => <span className="invalid">{message}</span>}
                          />
                        </div>
                      </Col>
                      {RATE_FIELDS.map(({ key, label }) => (
                        <Col md="12" key={key}>
                          <div className="form-group">
                            <label className="form-label">
                              {label} <span className="text-danger">*</span>
                            </label>
                            <div className="form-control-wrap">
                              <Controller
                                control={control}
                                name={`content.${key}`}
                                rules={{
                                  required: `Vui lòng nhập ${label.toLowerCase()}`,
                                  min: { value: 0, message: "Giá trị phải >= 0" },
                                  max: { value: 100, message: "Giá trị phải <= 100" },
                                }}
                                render={({ onChange, value }) => (
                                  <NumericFormat
                                    className="form-control"
                                    suffix=" %"
                                    decimalScale={2}
                                    value={value}
                                    onValueChange={(values) => onChange(values.floatValue ?? 0)}
                                  />
                                )}
                              />
                              <ErrorMessage
                                errors={errors}
                                name={`content.${key}`}
                                render={({ message }) => (
                                  <span className="invalid">{message}</span>
                                )}
                              />
                            </div>
                          </div>
                        </Col>
                      ))}
                      <Col md="12">
                        <Button color="primary" type="submit" disabled={loading}>
                          <span>{loading ? "Đang lưu..." : "Lưu"}</span>
                        </Button>
                        <Button
                          type="button"
                          color="light"
                          className="ml-2"
                          onClick={() => reset(DEFAULT_FORM)}
                        >
                          <span>Nhập mới</span>
                        </Button>
                      </Col>
                    </Row>
                  </form>
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <CardHeader className="bg-primary text-white">
                  Lịch sử tỷ lệ đã lưu
                </CardHeader>
                <CardBody>
                  <Table columns={columns} data={configs} notShowPagi></Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default IngredientExchange;
